import { useState, useEffect, useRef, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown, ChevronLeft, ChevronRight, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

type NavLink = { label: string; href: string; route?: boolean; hash?: string };
const navLinks: NavLink[] = [
  { label: "Pricing", href: "/pricing", route: true },
];

const companyLinks = [
  { label: "About us", href: "/about" },
  { label: "Blog", href: "/blog" },
];

const byDibsLinks = [{ label: "For Youth", href: "/foryouth" }];

const BOOK_A_DEMO_URL = "https://wa.me/6587169250";

const Navbar = ({ announcement }: { announcement?: ReactNode }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState<false | "platform" | "company">(false);
  const reducedMotion = useReducedMotion();
  const toggle = useRef<HTMLButtonElement>(null);
  const [megaOpen, setMegaOpen] = useState(false);
  const megaRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const [companyOpen, setCompanyOpen] = useState(false);
  const companyRef = useRef<HTMLDivElement>(null);
  const companyTimer = useRef<ReturnType<typeof setTimeout>>();

  const openMega = () => {
    clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const closeMegaSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(false), 120);
  };

  const openCompany = () => {
    clearTimeout(companyTimer.current);
    setCompanyOpen(true);
  };
  const closeCompanySoon = () => {
    clearTimeout(companyTimer.current);
    companyTimer.current = setTimeout(() => setCompanyOpen(false), 120);
  };

  useEffect(() => {
    if (!companyOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCompanyOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (!companyRef.current?.contains(event.target as Node)) setCompanyOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [companyOpen]);

  useEffect(() => {
    if (!megaOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMegaOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (!megaRef.current?.contains(event.target as Node)) setMegaOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [megaOpen]);

  const handleLogoClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    // On the home page the link is a no-op for the router, so scroll back to the top ourselves.
    if (window.location.pathname === "/") {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
    } else {
      window.scrollTo(0, 0);
    }
    setMobileOpen(false);
  };

  const handleNavClick = (link: NavLink) => {
    if (link.hash) {
      // If it's a hash link, navigate to home first if needed, then scroll
      if (window.location.pathname !== "/") {
        window.location.href = "/#" + link.hash;
      } else {
        // Already on home page, just scroll
        const element = document.getElementById(link.hash);
        const behavior = reducedMotion ? "auto" : "smooth";
        const stack = element?.parentElement;
        if (element && stack?.classList.contains("dh-feature-stack")) {
          // Feature cards are sticky, so their live position is where they stick,
          // not where the page needs to scroll. Aim for the card's natural slot
          // in the stack, offset by its sticky top, so it lands stuck in place.
          const index = Array.from(stack.children).indexOf(element);
          const gap = parseFloat(getComputedStyle(element).marginBottom) || 0;
          const stickyTop = parseFloat(getComputedStyle(element).top) || 0;
          const stackTop = stack.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: stackTop + index * (element.offsetHeight + gap) - stickyTop,
            behavior,
          });
        } else if (element) {
          element.scrollIntoView({ behavior });
        }
      }
    }
    setMobileOpen(false);
  };

  useEffect(() => {
    if (!mobileOpen) setMobileSub(false);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        toggle.current?.focus();
      }
    };
    const onResize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [mobileOpen]);

  return (
    <div
      className="fixed left-0 right-0 top-0 z-50 flex justify-center pointer-events-none px-4 md:px-8"
    >
      <motion.nav
        style={{ transitionDuration: reducedMotion ? "0ms" : undefined }}
        initial={reducedMotion ? false : { y: -100 }}
        aria-label="Main navigation"
        animate={{ y: 0 }}
        className="pointer-events-auto relative w-full max-w-[96rem] rounded-b-2xl overflow-visible bg-white/90 backdrop-blur-md border border-t-0 border-black/10 shadow-sm"
      >
        {announcement && <div className="-mx-px overflow-hidden">{announcement}</div>}
        <div className="w-full flex items-center justify-between px-4 md:px-8 py-2.5 md:py-3">
          <div className="flex items-center gap-8 md:gap-10">
            <Link
              to="/"
              onClick={handleLogoClick}
              className="flex items-center gap-2 group transition-opacity hover:opacity-80"
            >
              <img
                src="/dibs_assets/red_logo_with_transparent_bg.png"
                alt="Dibs"
                className="w-7 h-7 object-contain"
              />
              <span className="text-primary font-heading font-bold text-xl tracking-tight">
                Dibs
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <div
                ref={megaRef}
                onMouseEnter={openMega}
                onMouseLeave={closeMegaSoon}
              >
                <button
                  type="button"
                  aria-expanded={megaOpen}
                  aria-controls="platform-mega-menu"
                  onClick={() => setMegaOpen((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm font-heading font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Platform
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${megaOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {megaOpen && (
                  <div
                    id="platform-mega-menu"
                    className="absolute left-0 top-full pt-2"
                  >
                    <div className="w-[240px] rounded-xl border border-black/10 bg-white p-4 shadow-xl">
                      <div className="px-3 pb-2 text-xs font-semibold text-muted-foreground">
                        By Dibs
                      </div>
                      {byDibsLinks.map((f) => (
                        <Link
                          key={f.href}
                          to={f.href}
                          onClick={() => setMegaOpen(false)}
                          className="block rounded-md px-3 py-2 text-sm font-heading font-semibold text-foreground transition-colors hover:bg-muted focus-visible:bg-muted"
                        >
                          {f.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div
                ref={companyRef}
                onMouseEnter={openCompany}
                onMouseLeave={closeCompanySoon}
              >
                <button
                  type="button"
                  aria-expanded={companyOpen}
                  aria-controls="company-mega-menu"
                  onClick={() => setCompanyOpen((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm font-heading font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Company
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${companyOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {companyOpen && (
                  <div id="company-mega-menu" className="absolute left-0 top-full pt-2">
                    <div className="w-[240px] rounded-xl border border-black/10 bg-white p-4 shadow-xl">
                      <div className="px-3 pb-2 text-xs font-semibold text-muted-foreground">
                        Our company
                      </div>
                      {companyLinks.map((f) => (
                        <Link
                          key={f.href}
                          to={f.href}
                          onClick={() => setCompanyOpen(false)}
                          className="block rounded-md px-3 py-2 text-sm font-heading font-semibold text-foreground transition-colors hover:bg-muted focus-visible:bg-muted"
                        >
                          {f.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {navLinks.map((l) =>
                l.hash ? (
                  <button
                    key={l.href + l.hash}
                    onClick={() => handleNavClick(l)}
                    className="text-sm font-heading font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {l.label}
                  </button>
                ) : (
                  <Link
                    key={l.href}
                    to={l.href}
                    className="text-sm font-heading font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {l.label}
                  </Link>
                ),
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Desktop Buttons */}
            <div className="hidden md:flex items-center gap-4 md:gap-5">
              <Button
                asChild
                size="sm"
                className="px-5 shadow-md shadow-primary/20 font-semibold"
              >
                <a
                  href={BOOK_A_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book a demo
                </a>
              </Button>
              <Link
                to="/auth"
                className="group inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline underline-offset-4 transition-all"
              >
                Log in
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>

            {/* Mobile Book a demo */}
            <Button
              asChild
              variant="outline"
              size="sm"
              className="md:hidden px-4 font-semibold bg-white hover:bg-secondary hover:text-foreground"
            >
              <a
                href={BOOK_A_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
              >
                Book a demo
              </a>
            </Button>

            {/* Mobile toggle */}
            <button
              ref={toggle}
              type="button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="landing-mobile-menu"
              className="md:hidden text-foreground p-1 min-h-11 min-w-11 flex items-center justify-center"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu dropdown */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            transition={reducedMotion ? { duration: 0 } : undefined}
            id="landing-mobile-menu"
            initial={
              reducedMotion ? false : { opacity: 0, y: -20, scale: 0.95 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
              reducedMotion
                ? { opacity: 0 }
                : { opacity: 0, y: -20, scale: 0.95 }
            }
            className="pointer-events-auto bg-white md:hidden flex flex-col origin-top absolute top-[110%] left-4 right-4 border border-black/10 shadow-xl rounded-xl p-4 gap-4"
          >
            {mobileSub === "company" ? (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setMobileSub(false)}
                  className="flex items-center gap-2 min-h-11 pb-3 border-b border-black/10 text-sm font-heading font-semibold text-foreground text-left"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Go back
                </button>
                <span className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Our company</span>
                {companyLinks.map((f) => (
                  <Link
                    key={f.href}
                    to={f.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center min-h-11 text-sm font-heading font-semibold text-foreground"
                  >
                    {f.label}
                  </Link>
                ))}
              </div>
            ) : mobileSub === "platform" ? (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setMobileSub(false)}
                  className="flex items-center gap-2 min-h-11 pb-3 border-b border-black/10 text-sm font-heading font-semibold text-foreground text-left"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Go back
                </button>
                <span className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">By Dibs</span>
                {byDibsLinks.map((f) => (
                  <Link
                    key={f.href}
                    to={f.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center min-h-11 text-sm font-heading font-semibold text-foreground"
                  >
                    {f.label}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => setMobileSub("platform")}
                  className="flex items-center justify-between min-h-12 text-sm font-heading font-semibold text-foreground text-left"
                >
                  Platform
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setMobileSub("company")}
                  className="flex items-center justify-between min-h-12 text-sm font-heading font-semibold text-foreground text-left"
                >
                  Company
                  <ChevronRight className="h-4 w-4" />
                </button>
                {navLinks.map((l) =>
                  l.hash ? (
                    <button
                      key={l.href + l.hash}
                      onClick={() => handleNavClick(l)}
                      className="flex items-center justify-between min-h-12 text-sm font-heading font-semibold text-foreground text-left"
                    >
                      {l.label}
                    </button>
                  ) : (
                    <Link
                      key={l.href}
                      to={l.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between min-h-12 text-sm font-heading font-semibold text-foreground"
                    >
                      {l.label}
                    </Link>
                  ),
                )}
              </div>
            )}
            <div className="flex flex-col gap-2 pt-2">
              <Button
                asChild
                size="sm"
                className="w-full justify-center font-semibold"
              >
                <a
                  href={BOOK_A_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                >
                  Book a demo
                </a>
              </Button>
              <Link
                to="/auth"
                onClick={() => setMobileOpen(false)}
                className="group inline-flex items-center justify-center gap-1 text-sm font-semibold text-primary py-2"
              >
                Log in
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Navbar;
